import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import AlertBanner from '../../components/shared/AlertBanner';
import RestaurantMetricCard from '../../components/restaurants/RestaurantMetricCard';
import RestaurantFilters from '../../components/restaurants/RestaurantFilters';
import RestaurantTable from '../../components/restaurants/RestaurantTable';
import AddRestaurantModal, { RestaurantFormData } from '../../components/restaurants/AddRestaurantModal';
import { apiService, Restaurant, CreateRestaurantWithUserRequest } from '../../services/api';
import './Restaurants.css';

const Restaurants: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [stageFilter, setStageFilter] = useState('All Stages');
  const [searchQuery, setSearchQuery] = useState('');
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Fetch restaurants from API
  useEffect(() => {
    const abortController = new AbortController();

    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAllRestaurants(abortController.signal);
        
        // Handle different response structures
        let restaurantsArray: any[] = [];
        if (response.restaurants && Array.isArray(response.restaurants)) {
          restaurantsArray = response.restaurants;
        } else if (response.data && Array.isArray(response.data)) {
          restaurantsArray = response.data;
        } else if (Array.isArray(response)) {
          restaurantsArray = response;
        }

        if (restaurantsArray.length > 0) {
          // Map API restaurants to frontend restaurant format
          const mappedRestaurants = restaurantsArray.map(apiRestaurant => 
            apiService.mapApiRestaurantToRestaurant(apiRestaurant)
          );
          setRestaurants(mappedRestaurants);
        } else {
          setError('No restaurants found');
        }
      } catch (err: any) {
        // Ignore aborted requests
        if (err.name === 'AbortError') return;
        
        setError(err.message || 'An error occurred while fetching restaurants');
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();

    // Cleanup: cancel request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  const restaurantsPerPage = 8;

  // Filter restaurants based on search, status, location, and stage
  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = 
      restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      restaurant.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
      restaurant.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      restaurant.googlePlaceId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'All Status' || 
      restaurant.status === statusFilter.toLowerCase();

    const matchesLocation = locationFilter === 'All Locations' || 
      restaurant.city === locationFilter;

    // Stage filter can be used for verified status or other criteria
    const matchesStage = stageFilter === 'All Stages' || 
      (stageFilter === 'Verified' && restaurant.verified) ||
      (stageFilter === 'Unverified' && !restaurant.verified);

    return matchesSearch && matchesStatus && matchesLocation && matchesStage;
  });

  // Calculate metrics from filtered restaurants
  const totalRestaurants = filteredRestaurants.length;
  const activeRestaurants = filteredRestaurants.filter(r => r.status === 'active').length;
  const pendingRestaurants = filteredRestaurants.filter(r => r.status === 'pending').length;
  const inactiveRestaurants = filteredRestaurants.filter(r => r.status === 'inactive').length;

  // Paginate filtered restaurants
  const totalPages = Math.ceil(totalRestaurants / restaurantsPerPage);
  const startIndex = (currentPage - 1) * restaurantsPerPage;
  const endIndex = startIndex + restaurantsPerPage;
  const paginatedRestaurants = filteredRestaurants.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, locationFilter, stageFilter]);

  const handleSelectRestaurant = (restaurantId: string) => {
    setSelectedRestaurants(prev =>
      prev.includes(restaurantId)
        ? prev.filter(id => id !== restaurantId)
        : [...prev, restaurantId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRestaurants.length === paginatedRestaurants.length) {
      setSelectedRestaurants([]);
    } else {
      setSelectedRestaurants(paginatedRestaurants.map(r => r.id));
    }
  };

  const handleReviewNow = () => {
    // Navigate to pending restaurants or open review modal
    console.log('Review pending restaurants');
  };

  const handleAddRestaurant = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitRestaurant = async (data: RestaurantFormData) => {
    try {
      // Map form data to backend payload shape for /restaurants/admin/create-with-user
      const payload: CreateRestaurantWithUserRequest = {
        user: {
          phone: data.ownerPhone || undefined,
          email: data.ownerEmail || undefined,
          name: data.ownerName || undefined,
        },
        restaurant: {
          googlePlaceId: data.googlePlaceId,
          name: data.restaurantName,
          brandName: data.brandName?.trim() || undefined,
          description: data.description?.trim() || undefined,
          cuisineType: data.cuisineTypes && data.cuisineTypes.length > 0 ? data.cuisineTypes : undefined,
        },
        location: {
          address: data.streetAddress,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          phone: data.ownerPhone || undefined,
          timezone: undefined,
          lat: data.latitude ? Number(data.latitude) : undefined,
          lng: data.longitude ? Number(data.longitude) : undefined,
        },
      };

      await apiService.createRestaurantWithUser(payload);
      
      // Close modal and refresh restaurants list
      setIsModalOpen(false);
      // Optionally refresh the restaurants list
      // fetchRestaurants();
      
      // Show success message (you can add a toast notification here)
      alert('Restaurant created successfully!');
    } catch (err: any) {
      console.error('Error creating restaurant:', err);
      alert('Failed to create restaurant. Please try again.');
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="restaurants" />
        <main className="dashboard-content">
          {pendingRestaurants > 0 && (
            <AlertBanner
              message={`${pendingRestaurants} restaurant${pendingRestaurants !== 1 ? 's' : ''} awaiting review`}
              description="Review pending restaurants to activate them on the platform"
              actionLabel="Review Now"
              onAction={handleReviewNow}
            />
          )}

          <div className="restaurants-header-section">
            <div>
              <h1 className="restaurants-title">Restaurants</h1>
              <p className="restaurants-subtitle">Manage restaurant partners</p>
            </div>
            <button className="restaurants-add-btn" onClick={handleAddRestaurant}>
              + Add Restaurant
            </button>
          </div>

          <div className="restaurants-metrics">
            <RestaurantMetricCard
              title="Total Restaurants"
              value={totalRestaurants.toLocaleString()}
              color="default"
            />
            <RestaurantMetricCard
              title="Active"
              value={activeRestaurants.toLocaleString()}
              color="green"
            />
            <RestaurantMetricCard
              title="Pending Approval"
              value={pendingRestaurants.toLocaleString()}
              color="orange"
            />
            <RestaurantMetricCard
              title="Inactive"
              value={inactiveRestaurants.toLocaleString()}
              color="gray"
            />
          </div>

          <RestaurantFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            locationFilter={locationFilter}
            onLocationFilterChange={setLocationFilter}
            stageFilter={stageFilter}
            onStageFilterChange={setStageFilter}
          />

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading restaurants...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
              <p>Error: {error}</p>
            </div>
          ) : (
            <RestaurantTable
              restaurants={paginatedRestaurants}
              selectedRestaurants={selectedRestaurants}
              onSelectRestaurant={handleSelectRestaurant}
              onSelectAll={handleSelectAll}
            />
          )}

          {!loading && !error && totalRestaurants > 0 && (
            <div className="restaurants-pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * restaurantsPerPage) + 1} to {Math.min(currentPage * restaurantsPerPage, totalRestaurants)} of {totalRestaurants} restaurants
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {!loading && !error && totalRestaurants === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>No restaurants found.</p>
            </div>
          )}
        </main>
      </div>
      <AddRestaurantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitRestaurant}
      />
    </div>
  );
};

export default Restaurants;

